import Moment from 'moment';
import { DateRange, extendMoment } from 'moment-range';

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const moment = extendMoment(Moment as any);

type DateLike = Date | Moment.Moment;

export type RangeInvalidReason = 'incomplete' | 'inverted' | 'beforeMin' | 'afterMax' | 'tooLong';

interface RangeValidationParams {
  from?: DateLike;
  to?: DateLike;
  /** Earliest selectable day. May carry a time of day, so it is compared as a whole day. */
  minDate?: Date;
  /** Latest selectable day. May carry a time of day, so it is compared as a whole day. */
  maximumDate?: Date;
  /** Inclusive day span limit: 180 means `from … from + 179 days`. */
  maxDaysCount?: number;
  /** Treat a half picked range as invalid instead of ignoring the missing endpoint. */
  requireComplete?: boolean;
}

/**
 * Validates a picked range against its bounds, returning why it is invalid or `null` when it is fine.
 *
 * Bounds come in as timestamps (an audience creation date, "now") while the picker works in whole
 * days, so every comparison is made at day granularity.
 */
export const getRangeInvalidReason = ({
  from,
  to,
  minDate,
  maximumDate,
  maxDaysCount,
  requireComplete = false,
}: RangeValidationParams): RangeInvalidReason | null => {
  const start = from ? moment(from) : null;
  const end = to ? moment(to) : null;

  if (requireComplete && (!start || !end)) return 'incomplete';
  if (start && end && start.isAfter(end, 'day')) return 'inverted';
  if (maximumDate && (start?.isAfter(maximumDate, 'day') || end?.isAfter(maximumDate, 'day'))) return 'afterMax';
  if (minDate && (start?.isBefore(minDate, 'day') || end?.isBefore(minDate, 'day'))) return 'beforeMin';
  if (start && end && exceedsMaxDays({ from: start, to: end, maxDaysCount })) return 'tooLong';

  return null;
};

/**
 * Clamps `range` into `[minDate, maximumDate]`, comparing whole days like the rest of the picker.
 *
 * Returns `null` when the range sits entirely outside the selectable window, which a predefined
 * selection does as soon as `minDate` moves past it ("last month" on an audience created this
 * month). Callers must treat that as "no selection" instead of feeding it back into the picker.
 */
export const clampRangeToBounds = ({
  range,
  minDate,
  maximumDate,
}: {
  range: DateRange;
  minDate?: Date;
  maximumDate?: Date;
}): DateRange | null => {
  const start = minDate && range.start.isBefore(minDate, 'day') ? moment(minDate) : range.start;
  const end = maximumDate && range.end.isAfter(maximumDate, 'day') ? moment(maximumDate) : range.end;

  if (end.isBefore(start, 'day')) return null;

  return moment.range(start, end);
};

/** Inclusive day count between two dates, so a single day range counts as 1. */
export const countDaysInRange = (from: DateLike, to: DateLike) =>
  moment(to).startOf('day').diff(moment(from).startOf('day'), 'days') + 1;

export const exceedsMaxDays = ({ from, to, maxDaysCount }: { from: DateLike; to: DateLike; maxDaysCount?: number }) => {
  if (!maxDaysCount) return false;

  return countDaysInRange(from, to) > maxDaysCount;
};
